import { useState } from 'react';
import { Download } from '@carbon/react/icons';
import { CalculationDetails } from 'components/calculation-details';
import { Date } from 'components/date';
import { FormattedCount } from 'components/formatted-count';
import { LaunchLink } from 'components/launch-link';
import { useDownloadResource } from 'lib/hooks/useDownloadFile';
import type { CardWorkspace } from '../../types';
import { format2 } from 'utils/number';
import styles from './workspace-card.module.scss';

const noop = () => {};

// legal_acreage is a Postgres numeric serialized as a JSON string; format2
// parses the string and applies 2-decimal formatting.
const acreage = (value?: number | string | null) => format2(value, '—');

const GraphLink = ({
  href,
  label,
}: {
  href?: string | null;
  label: string;
}) => {
  if (!href) return null;
  return (
    <LaunchLink
      to={href}
      className={styles.graphLink}
      label={`${label} — open in a new tab`}
      iconSize={14}
      stopClickPropagation
    >
      {label}
    </LaunchLink>
  );
};

// One title workspace: a Download Ownership action, its calculation metadata
// (summary line + detail line) + graph links, then the reused Ownerships table
// (lazy-loads from the workspace href + interpretation id).
export const WorkspaceCard = ({
  workspace,
  refsMap,
}: {
  workspace: CardWorkspace;
  refsMap: Map<number, HTMLElement | null | undefined>;
}) => {
  const graphs = workspace.graphs ?? {};
  const download = useDownloadResource(workspace.downloadHref ?? undefined);
  // Download Ownership is shown only once the ownerships fetch confirms there are
  // rows — no ownership data, no download action.
  const [hasOwnershipData, setHasOwnershipData] = useState(false);
  return (
    <div className={styles.workspace}>
      {workspace.downloadHref && hasOwnershipData && (
        <button
          type="button"
          className={styles.downloadLink}
          disabled={download.generatingDownload}
          onClick={(e) => {
            e.stopPropagation();
            download.triggerDownload();
          }}
        >
          Download Ownership
          <Download size={14} />
        </button>
      )}

      <div className={styles.metaBlock}>
        {/* Summary line: the last-calc timestamp, as-of date and total acreage. */}
        <dl className={styles.meta}>
          <div>
            <dt>Last Calculated</dt>
            <dd>
              <Date
                date={workspace.lastCalculated ?? undefined}
                format="lll"
                showTime
                fallback="—"
              />
            </dd>
          </div>
          <div>
            <dt>With As-of Date</dt>
            <dd>
              <Date date={workspace.asOfDate ?? undefined} fallback="—" />
            </dd>
          </div>
          <div>
            <dt>Total Title GIS Gross Acreage</dt>
            <dd>{acreage(workspace.totalGrossAcreage)}</dd>
          </div>
        </dl>

        {/* Detail line: the workspace metadata. */}
        <dl className={styles.meta}>
          <div>
            <dt>Created By</dt>
            <dd>{workspace.createdBy ?? '—'}</dd>
          </div>
          <div>
            <dt>Created At</dt>
            <dd>
              <Date date={workspace.createdAt ?? undefined} fallback="—" />
            </dd>
          </div>
          <div>
            <dt>Owners</dt>
            <dd>
              <FormattedCount value={workspace.numberOfOwners} fallback="—" />
            </dd>
          </div>
          <div>
            <dt>Subtracts</dt>
            <dd>
              <FormattedCount value={workspace.numberOfTracts} fallback="—" />
            </dd>
          </div>
          <div>
            <dt>Severances</dt>
            <dd>
              <FormattedCount value={workspace.numberOfDepths} fallback="—" />
            </dd>
          </div>
          <div>
            <dt>Starting Interest</dt>
            <dd>
              <Date
                date={workspace.startingInterestDate ?? undefined}
                fallback="—"
              />
            </dd>
          </div>
          <div>
            <dt>Legal Acreage</dt>
            <dd>{acreage(workspace.legalAcreage)}</dd>
          </div>
          <div>
            <dt>Survey Area</dt>
            <dd>{workspace.surveyArea ?? '—'}</dd>
          </div>
        </dl>
      </div>

      <div className={styles.graphs}>
        <GraphLink
          href={graphs.runsheetTimelineHref}
          label="Runsheet Timeline"
        />
      </div>

      <CalculationDetails
        titleWorkspace={workspace.id}
        titleWorkspaceHref={workspace.href}
        referenceLocationHref={workspace.referenceLocationHref ?? ''}
        interpretationId={workspace.interpretationId}
        refsMap={refsMap}
        setHighlightedRow={noop}
        variant="minimal"
        extendedColumns
        emptyPlaceholder="No workspace calculation available for this document."
        heading="Ownerships"
        headingClassName={styles.ownershipsHeading}
        onDataPresence={setHasOwnershipData}
      />
    </div>
  );
};
